import { useRef, useState, type DragEvent } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Upload, Search, Trash2, Image as ImageIcon, FileText, Film,
  Copy, Pencil, Loader2, UploadCloud,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, unwrap } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input, Label, Textarea } from '@/components/ui/Input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/Dialog';
import { Badge } from '@/components/ui/Badge';
import { FocalPointPicker, type FocalPoint } from '@/features/media/FocalPointPicker';

interface MediaAsset {
  id: string;
  url: string;
  thumb_url: string | null;
  original_name: string;
  title: string | null;
  mime_type: string;
  size: number;
  width: number | null;
  height: number | null;
  alt: string | null;
  caption: string | null;
  tags: string[] | null;
  placeholder: string | null;
  focal_point: FocalPoint | null;
  optimized_at: string | null;
  created_at: string;
}

interface Paginated<T> {
  data: T[];
  total: number;
  current_page: number;
  last_page: number;
}

type MediaType = 'all' | 'image' | 'video' | 'application';

export const Route = createFileRoute('/_app/media/')({
  component: MediaLibraryPage,
});

function MediaLibraryPage() {
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState('');
  const [type, setType] = useState<MediaType>('all');
  const [page, setPage] = useState(1);
  const [dragging, setDragging] = useState(false);
  const [selected, setSelected] = useState<MediaAsset | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['media', q, type, page],
    queryFn: () => {
      const params = new URLSearchParams({ per_page: '30', page: String(page) });
      if (q) params.set('q', q);
      if (type !== 'all') params.set('type', type);
      return unwrap<Paginated<MediaAsset>>(api.get(`/media?${params}`));
    },
  });

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      let ok = 0;
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        try {
          await api.post('/media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          ok++;
        } catch {
          toast.error(`No se pudo subir ${file.name}`);
        }
      }
      return ok;
    },
    onSuccess: (ok) => {
      if (ok > 0) toast.success(ok === 1 ? 'Archivo subido' : `${ok} archivos subidos`);
      void qc.invalidateQueries({ queryKey: ['media'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/media/${id}`),
    onSuccess: () => {
      toast.success('Eliminado');
      setSelected(null);
      void qc.invalidateQueries({ queryKey: ['media'] });
    },
    onError: () => toast.error('No se pudo eliminar'),
  });

  function handleFiles(list: FileList | null) {
    const files = Array.from(list ?? []);
    if (files.length) upload.mutate(files);
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  }

  async function copyUrl(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('URL copiada');
    } catch {
      toast.error('No se pudo copiar');
    }
  }

  const items = data?.data ?? [];

  return (
    <div className="mx-auto max-w-7xl p-6 md:p-8">
      <header className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Multimedia</h1>
          <p className="text-sm text-neutral-500">Imágenes, vídeos y documentos reutilizables.</p>
        </div>
        <Button onClick={() => fileInput.current?.click()} disabled={upload.isPending}>
          {upload.isPending ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
          {upload.isPending ? 'Subiendo…' : 'Subir archivos'}
        </Button>
        <input
          ref={fileInput}
          type="file"
          multiple
          className="hidden"
          accept="image/*,video/mp4,application/pdf"
          onChange={(e) => {
            handleFiles(e.target.files);
            if (fileInput.current) fileInput.current.value = '';
          }}
        />
      </header>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-400" />
          <Input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Buscar por nombre o texto alternativo…"
            className="pl-9"
          />
        </div>
        <select
          value={type}
          onChange={(e) => { setType(e.target.value as MediaType); setPage(1); }}
          className="h-9 rounded-md border border-neutral-200 px-3 text-sm"
        >
          <option value="all">Todos los tipos</option>
          <option value="image">Imágenes</option>
          <option value="video">Vídeos</option>
          <option value="application">Documentos</option>
        </select>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={[
          'relative rounded-xl border-2 border-dashed p-3 transition-colors',
          dragging ? 'border-[#AD026A] bg-[#AD026A]/5' : 'border-neutral-200',
        ].join(' ')}
      >
        {dragging && (
          <div className="pointer-events-none absolute inset-0 z-10 grid place-items-center rounded-xl bg-white/70">
            <div className="flex flex-col items-center gap-2 text-[#AD026A]">
              <UploadCloud className="size-8" />
              <span className="text-sm font-medium">Suelta los archivos para subirlos</span>
            </div>
          </div>
        )}

        {isLoading && <div className="p-8 text-center text-sm text-neutral-400">Cargando…</div>}

        {!isLoading && items.length === 0 && (
          <div className="flex flex-col items-center gap-2 p-12 text-center">
            <div className="grid size-12 place-items-center rounded-full bg-neutral-100">
              <ImageIcon className="size-6 text-neutral-400" />
            </div>
            <p className="text-sm font-medium text-neutral-700">No hay archivos todavía</p>
            <p className="text-xs text-neutral-400">Arrastra archivos aquí o usa el botón “Subir archivos”.</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
          {items.map((m) => (
            <div
              key={m.id}
              className="group relative cursor-pointer overflow-hidden rounded-lg border border-neutral-200 bg-white transition-shadow hover:shadow-md"
              onClick={() => setSelected(m)}
            >
              <AssetThumb asset={m} />
              <div className="p-2 text-xs">
                <div className="truncate font-medium">{m.original_name}</div>
                <div className="text-neutral-500">
                  {(m.size / 1024).toFixed(0)}&nbsp;KB
                  {m.width && m.height ? ` · ${m.width}×${m.height}` : ''}
                </div>
              </div>
              <div className="absolute right-1 top-1 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                <button
                  onClick={(e) => { e.stopPropagation(); void copyUrl(m.url); }}
                  className="rounded bg-white/90 p-1 text-neutral-700 hover:text-neutral-900"
                  aria-label="Copiar URL"
                >
                  <Copy className="size-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm(`¿Eliminar "${m.original_name}"?`)) remove.mutate(m.id);
                  }}
                  className="rounded bg-white/90 p-1 text-red-600 hover:text-red-700"
                  aria-label="Eliminar"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {data && data.last_page > 1 && (
        <div className="mt-4 flex items-center justify-between text-xs text-neutral-500">
          <span>Página {data.current_page} de {data.last_page} · {data.total} archivos</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
            <Button size="sm" variant="outline" disabled={page >= data.last_page} onClick={() => setPage(page + 1)}>Siguiente</Button>
          </div>
        </div>
      )}

      <AssetDialog
        asset={selected}
        onClose={() => setSelected(null)}
        onCopy={copyUrl}
        onDelete={(id) => { if (confirm('¿Eliminar este archivo?')) remove.mutate(id); }}
      />
    </div>
  );
}

function AssetThumb({ asset }: { asset: MediaAsset }) {
  if (asset.mime_type.startsWith('image/')) {
    return (
      <div
        className="aspect-square w-full bg-neutral-100 bg-cover bg-center"
        style={asset.placeholder ? { backgroundImage: `url(${asset.placeholder})` } : undefined}
      >
        {/* Usa la miniatura WebP generada (ligera); cae a la original si no hay. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={asset.thumb_url ?? asset.url}
          alt={asset.alt ?? asset.original_name}
          className="aspect-square w-full object-cover"
          loading="lazy"
        />
      </div>
    );
  }
  const Icon = asset.mime_type.startsWith('video/') ? Film : FileText;
  return (
    <div className="grid aspect-square place-items-center bg-neutral-100">
      <Icon className="size-8 text-neutral-400" />
    </div>
  );
}

interface AssetDialogProps {
  asset: MediaAsset | null;
  onClose: () => void;
  onCopy: (url: string) => void;
  onDelete: (id: string) => void;
}

function AssetDialog({ asset, onClose, onCopy, onDelete }: AssetDialogProps) {
  return (
    <Dialog open={asset != null} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        {asset && (
          <AssetEditor key={asset.id} asset={asset} onClose={onClose} onCopy={onCopy} onDelete={onDelete} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function AssetEditor({ asset, onClose, onCopy, onDelete }: { asset: MediaAsset } & Omit<AssetDialogProps, 'asset'>) {
  const qc = useQueryClient();
  const [alt, setAlt] = useState(asset.alt ?? '');
  const [name, setName] = useState(asset.original_name);
  const [title, setTitle] = useState(asset.title ?? '');
  const [caption, setCaption] = useState(asset.caption ?? '');
  const [tags, setTags] = useState((asset.tags ?? []).join(', '));
  const [focal, setFocal] = useState<FocalPoint>(asset.focal_point ?? { x: 0.5, y: 0.5 });

  const isImage = asset.mime_type.startsWith('image/');
  const isVideo = asset.mime_type.startsWith('video/');

  const save = useMutation({
    mutationFn: () => api.put(`/media/${asset.id}`, {
      alt,
      title: title || null,
      caption: caption || null,
      original_name: name,
      tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
      ...(isImage ? { focal_point: focal } : {}),
    }),
    onSuccess: () => {
      toast.success('Cambios guardados');
      void qc.invalidateQueries({ queryKey: ['media'] });
      onClose();
    },
    onError: () => toast.error('No se pudo guardar'),
  });

  return (
    <>
      <DialogHeader>
        <DialogTitle className="truncate">{asset.original_name}</DialogTitle>
        <DialogDescription className="flex flex-wrap items-center gap-2">
          <span>
            {asset.mime_type} · {(asset.size / 1024).toFixed(0)} KB
            {asset.width && asset.height ? ` · ${asset.width}×${asset.height}` : ''}
          </span>
          {asset.optimized_at && <Badge variant="success">Optimizada</Badge>}
        </DialogDescription>
      </DialogHeader>

      <div className="grid gap-4 sm:grid-cols-2">
        {isImage ? (
          <FocalPointPicker src={asset.thumb_url ?? asset.url} value={focal} onChange={setFocal} />
        ) : (
          <div className="grid h-64 place-items-center overflow-hidden rounded-lg border border-neutral-200 bg-neutral-50">
            {isVideo ? <Film className="size-12 text-neutral-400" /> : <FileText className="size-12 text-neutral-400" />}
          </div>
        )}

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="m-url">URL</Label>
            <div className="flex gap-2">
              <Input id="m-url" value={asset.url} readOnly className="text-xs" />
              <Button size="sm" variant="outline" onClick={() => onCopy(asset.url)} aria-label="Copiar URL">
                <Copy className="size-4" />
              </Button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="m-name">Nombre del archivo</Label>
            <Input id="m-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="m-title">Título</Label>
            <Input id="m-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título descriptivo (opcional)" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="m-alt">Texto alternativo (alt)</Label>
            <Textarea
              id="m-alt"
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              rows={2}
              placeholder="Describe la imagen para accesibilidad y SEO"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="m-caption">Pie de foto</Label>
            <Input id="m-caption" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Pie / créditos (opcional)" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="m-tags">Etiquetas (separadas por comas)</Label>
            <Input id="m-tags" value={tags} onChange={(e) => setTags(e.target.value)} />
          </div>
        </div>
      </div>

      <DialogFooter className="gap-2 sm:justify-between">
        <Button
          variant="ghost"
          className="text-red-600 hover:bg-red-50 hover:text-red-700"
          onClick={() => onDelete(asset.id)}
        >
          <Trash2 className="size-4" /> Eliminar
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Guardando…' : <><Pencil className="size-4" /> Guardar</>}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}
